import { Component, ElementRef, Input, OnInit, EventEmitter, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ToastService } from 'src/app/services/toast.service';
import { checkForValidationErrors } from 'src/app/utils/checkForValidationErrors';
import { Router } from '@angular/router';
import { scrollToFirstInvalidControl } from 'src/app/utils/scrollToFirstInvalidControl';
import { AsyncDropdown } from 'src/app/models/async-dropdown';
import { toggleBlanket } from 'src/app/utils/toggleBlanket';
import { clearErrors } from 'src/app/utils/clearFormErrors';
import { OrderService } from '../service/order.service';
import { NejoData } from '../models/nejo-data';
import { LovApiService } from 'src/app/services/lov-api.service';
import { SortObject } from 'src/app/models/sortObject';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { JiraUserService } from '../../jira-users/service/jira-user.service';
@Component({
  selector: 'nejo-order-data',
  templateUrl: './data.component.html',
  styleUrls: ['./data.component.scss'],
  providers: [ JiraUserService ]
})
export class DataComponent implements OnInit {
  @Input() data: NejoData = new NejoData();
  @Input() budgets: any = [];
  @Input() private createOrder: EventEmitter<boolean>;
  @Output() createOrderSuccess = new EventEmitter<boolean>(false);
  @Input() isCreate: boolean = false;
  public form: UntypedFormGroup = null;
  public submitFailed = false;
  @Input() editForm: boolean = false;
  public orderId: string;
  // dropdowns
  public customer: AsyncDropdown[] = [];
  public customerLoading: boolean = false;
  public orderStatus: AsyncDropdown[] = [];
  public orderStatusLoading: boolean = false;
  public lob: AsyncDropdown[] = [];
  public lobLoading: boolean = false;
  public pillar: AsyncDropdown[] = [];
  public pillarLoading: boolean = false;
  public solution: AsyncDropdown[] = [];
  public solutionLoading: boolean = false;
  public opportunity: AsyncDropdown[] = [];
  public opportunityLoading: boolean = false;
  public owner: AsyncDropdown[] = [];
  public ownerLoading: boolean = false;
  tags: Array<string> = [];
  tagsSuggestion: Array<string> = [];
  disableDeleteConfirmButton: boolean = false;
  userSelected: boolean = false;

  public deleteOrderText: string = 'Are you sure to delete this order?';
  public isQuestion: boolean = true;

  /* #708 */
  quickSearch = new Subject<any>();
  queryString: string = '';
  searchValue: string = '';
  result: any = '';
  isLoading: boolean = false;
  /* #708 END */ 
  constructor(
    private orderService: OrderService,
    private toastService: ToastService,
    private lovApiService: LovApiService,
    private el: ElementRef,
    private router: Router,
    private jiraService: JiraUserService
  ) {
    this.initForm(this.data);
    this.quickSearch.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((input) => {
      let value = "";
      if(input != null && typeof input != typeof undefined){
        if(input.value != null && typeof input.value != typeof undefined){
          value = input.value;
        }
      }
      this.search(value);
    });
  }

  async ngOnInit() {
    if (!this.isCreate) {
      this.orderId = this.router.url.split('/')[2];
      if (this.orderId.includes('#')) {
        this.orderId = this.orderId.split('#')[0];
        setTimeout(
          () =>
            document
              .getElementById('projects')
              .scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' }),
          300
        );
      }
      this.data = await this.orderService.getNejoData(this.orderId);
    } else {
      this.createOrder.subscribe((data) => this.saveForm());
    }
    this.initForm(this.data);
  }

  initForm(data: NejoData) {
    this.initDropdowns(data);

    if (this.isCreate) {
      this.form = new UntypedFormGroup({
        name: new UntypedFormControl(null, [Validators.required]),
        customer: new UntypedFormControl(null, [Validators.required]),
      });
    } else {
      this.form = new UntypedFormGroup({
        identifier: new UntypedFormControl(data?.identifier),
        code: new UntypedFormControl(data?.code, [Validators.required]),
        name: new UntypedFormControl(data?.name, [Validators.required]),
        link: new UntypedFormControl(data?.link),
        source: new UntypedFormControl(data?.source),
        customer: new UntypedFormControl(data?.customer, [Validators.required]),
        modifiedAt: new UntypedFormControl({ value: data?.modifiedAt, disabled: true }),
        createdAt: new UntypedFormControl({ value: data?.createdAt, disabled: true }),
        offer: new UntypedFormControl(data?.offer),
        ownerSales: new UntypedFormControl(data?.ownerSales),
        owner: new UntypedFormControl(data?.owner),
        ownerPresales: new UntypedFormControl(data?.ownerPresales),
        ownerOrderManagement: new UntypedFormControl(data?.ownerOrderManagement),
        ownerOperationsManager: new UntypedFormControl(data?.ownerOperationsManager),
        opportunity: new UntypedFormControl(data?.opportunity),
        lob: new UntypedFormControl(data?.lob),
        pillar: new UntypedFormControl(data?.pillar),
        solution: new UntypedFormControl(data?.solution),
        orderStatus: new UntypedFormControl(data?.orderStatus),
        tags: new UntypedFormControl(data?.tags),
        ownerHandler: new UntypedFormControl(data?.ownerHandler),
      });
      this.form.controls['modifiedAt']?.disable();
      this.form.controls['createdAt']?.disable();
      
      this.searchValue = data?.ownerHandler;
    }
  }

  initDropdowns(data: NejoData) {
    this.tags = data.tags ? data.tags : [];

    this.customer.push(data.customer || new AsyncDropdown());
    this.orderStatus.push(data.orderStatus || new AsyncDropdown());
    this.lob.push(data.lob || new AsyncDropdown());
    this.solution.push(data.solution || new AsyncDropdown());
    this.pillar.push(data.pillar || new AsyncDropdown());
    this.opportunity.push(data.opportunity || new AsyncDropdown());
  }

  async saveForm() {
    try {
      if (this.form.valid || this.submitFailed) {
        this.submitFailed = false;
        if (!this.isCreate) {
          this.form.controls.tags.patchValue(this.tags);
          this.data = await this.orderService.updateNejoData(this.form?.value);
          this.editForm = false;
          this.initForm(this.data);
          this.toastService.showSuccess('Operation success!');
          toggleBlanket('nejo-order-data');
          this.form.markAsUntouched();
        } else {
          this.data = await this.orderService.createOrder(this.form?.value);
          this.createOrderSuccess.emit(true);
          this.toastService.showSuccess('Operation success!');
          this.router.navigate(['order/' + this.data.identifier]);
        }
      } else {
        this.toastService.showError('Operation error!');
        this.form.markAsPristine();
        this.form.markAllAsTouched();
      }
    } catch (e) {
      clearErrors(this.form);
      this.submitFailed = true;
      this.toastService.showError('Operation error!');
      if (e.validationErrors.length > 0) {
        this.form.markAsPristine();
        checkForValidationErrors(e, this.form);
        scrollToFirstInvalidControl(this.el);
      }
    }
  }

  existForm(){
    if(this.form != null && typeof this.form != typeof undefined){
      return true;
    }
    return false;
  }

  async cancelForm() {
    this.data = await this.orderService.getNejoData(this.router.url.split('/')[2]);
    this.initForm(this.data);
  }

  editFormEvent(event) {
    this.editForm = event;
    toggleBlanket('nejo-order-data');
  }

  async fetchCustomer() {
    this.customer = [];
    this.customerLoading = true;
    var sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.customer = await this.lovApiService.getDropdownValues('order', 'customer', sort);
    this.customerLoading = false;
  }

  async fetchOrderStatus() {
    this.orderStatus = [];
    this.orderStatusLoading = true;
    var sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.orderStatus = await this.lovApiService.getDropdownValues('order', 'status', sort);
    this.orderStatusLoading = false;
  }

  async fetchLob() {
    this.lob = [];
    this.lobLoading = true;
    var sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.lob = await this.lovApiService.getDropdownValues('order', 'lob', sort);
    this.lobLoading = false;
  }

  async fetchPillar() {
    this.pillar = [];
    this.pillarLoading = true;
    var sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.pillar = await this.lovApiService.getDropdownValues('order', 'pillar', sort);
    this.pillarLoading = false;
  }

  async fetchSolution() {
    this.solution = [];
    this.solutionLoading = true;
    var sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.solution = await this.lovApiService.getDropdownValues('order', 'solution', sort);
    this.solutionLoading = false;
  }

  async fetchOpportunity() {
    this.opportunity = [];
    this.opportunityLoading = true;
    var sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.opportunity = await this.lovApiService.getDropdownValues('order', 'opportunity', sort);
    this.opportunityLoading = false;
  }

  async fetchOwner() {
    this.owner = [];
    this.ownerLoading = true;
    var sort: SortObject[] = [{ prop: 'email', dir: 'asc' }];
    this.owner = await this.lovApiService.getDropdownValues('order', 'owner', sort);
    this.ownerLoading = false;
  }

  getTags = (text: string): Observable<any> => {
    if (text.length > 0) {
      return this.orderService.getTags(text.toLowerCase()).pipe(
        map((tagsSuggestion) =>
          tagsSuggestion.map((item: any) => {
            return item.value + ' (' + item.count + ')';
          })
        )
      );
    }
  };

  public transform(value): Observable<object> {
    var item;
    if (value && value.display) {
      var formattedValue = value.display;
      var formattedValue = value.display.split(' (')[0];
      item = { display: `${formattedValue}`, value: `${formattedValue}` };
    } else {
      item = { display: value, value: value };
    }
    return of(item);
  }

  public preDeleteOrder() {
    var projectLinkedNumber = this.budgets.value.filter((el) => el.project && el.project.identifier !== null).length;
    let name = "";
    let code = "";
    let source = "";
    if(this.existForm()){
      name = this.form?.value?.name;
      code = this.form?.value?.code;
      source = this.form?.value?.source;
    }
    if (projectLinkedNumber > 0) {
      this.deleteOrderText =
        'Order <b>' + name +
        '</b> (' + code +
        ') has <b>' + projectLinkedNumber +
        ' </b> budgets with linked project. <br/> You need to manually unlink (or delete) all the linked projects in order to delete this order.';
      this.isQuestion = false;
    } else {
      this.isQuestion = true;
      this.deleteOrderText =
        'Are you sure to delete the Order <b>' + name + '</b> (' + code + ')?';
    }
    if (source === 'CRM') {
      this.deleteOrderText +=
        "<br/> <br/> <img class='icon mr-2 crm-warning' src='assets/ico/exclamation-triangle-regular-red.svg' />  Order is sourced by the external CRM.";
    }
  }
  public deleteOrder() {
    this.orderService.deleteOrder(this.orderId).then(() => {
      this.router.navigateByUrl('/dashboard/orders');
      this.toastService.showSuccess("Order successfully deleted!")
      
    });
  }

  
  async search(value: string) {
    this.searchValue = value;
    this.queryString = this.searchValue;
    this.isLoading = true;
    this.result = [];
    this.userSelected = false;
    if (this.queryString.length > 0) {
      this.jiraService.getJiraUsers(this.queryString).then(
        (res) => {
          this.result = res;
          this.isLoading = false;
        },
        (reason) => {
          this.isLoading = false;
        }
      );
    } else {
      this.result = [];
      this.isLoading = false;
    }
  }

  closeSearch() {
    this.result = [];
    this.searchValue = '';
    this.queryString = '';
    this.isLoading = false;
    this.form.controls.ownerHandler.reset();
    this.form.markAsDirty();
  }

  selectUser(user) {
    this.form.markAsDirty();
    this.form.controls.ownerHandler.patchValue(user.email);
    this.searchValue = user.email;
    this.userSelected = true;
  }

  constructDisplayName(user) {
    var uid = '';
    if (user.uid.length) {
      uid = user.uid + ' | ';
    }
    var department = '';
    if (user.department.length) {
      department = user.department + ' | ';
    }
    return '<b>' + user.name + '</b>' + ' (' + user.email + ') <br/>' + uid + department + user.creationDate;
  }

}
